import axios from "axios";
import useSWR, { type KeyedMutator } from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import {
  DEFAULT_V2_SEARCH_CHOICES,
  V2_SEARCH_CHOICES_URL,
  V2_SEARCH_URL,
  fetchV2SearchChoices,
  searchV2,
  type V2SearchChoices,
  type V2SearchRequest,
  type V2SearchResponse,
} from "../api/v2Search";

/** Messages a failed search shows; 400 lists the server's per-field messages. */
export interface V2SearchError {
  kind: "invalid" | "timeout" | "other";
  messages: string[];
}

const collectMessages = (value: unknown): string[] => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(collectMessages);
  if (value != null && typeof value === "object")
    return Object.values(value).flatMap(collectMessages);
  return [];
};

export const toV2SearchError = (error: unknown): V2SearchError => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (status === 400) {
    const messages = collectMessages(
      axios.isAxiosError(error) ? error.response?.data : undefined
    );
    return {
      kind: "invalid",
      messages:
        messages.length > 0 ? messages : ["検索条件を確認してください。"],
    };
  }
  if (status === 422 || status === 503 || status === 504) {
    return {
      kind: "timeout",
      messages: [
        "検索に時間がかかりすぎたため中断しました。条件を絞り込んでもう一度検索してください。",
      ],
    };
  }
  return {
    kind: "other",
    messages: [
      "検索結果を取得できませんでした。時間をおいて再試行してください。",
    ],
  };
};

export const useV2Search = (
  request: V2SearchRequest | null
): {
  data: V2SearchResponse | undefined;
  error: V2SearchError | undefined;
  isLoading: boolean;
  isValidating: boolean;
  mutate: KeyedMutator<V2SearchResponse>;
} => {
  const { apiClient } = useApiClient();
  // The request body is the cache key; serialise it so equal conditions share an entry.
  const { data, error, isLoading, isValidating, mutate } = useSWR<
    V2SearchResponse,
    unknown
  >(
    request === null ? null : [V2_SEARCH_URL, JSON.stringify(request)],
    async ([, body]: [string, string]) =>
      await searchV2(apiClient, JSON.parse(body) as V2SearchRequest),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );
  return {
    data,
    error: error === undefined ? undefined : toV2SearchError(error),
    isLoading,
    isValidating,
    mutate,
  };
};

/** Falls back to the fixed vocabularies so the form works without the choices API. */
export const useV2SearchChoices = (): V2SearchChoices => {
  const { apiClient } = useApiClient();
  const { data } = useSWR<V2SearchChoices>(
    V2_SEARCH_CHOICES_URL,
    async () => await fetchV2SearchChoices(apiClient),
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );
  return data ?? DEFAULT_V2_SEARCH_CHOICES;
};
